import type { Session, User } from '@sentra/contracts';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { ApiClient } from '@/api/client';
import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/errors';
import { apiRoutes } from '@/api/routes';
import { useLocale } from '@/i18n';

/**
 * Who is signed in, what the terminal is allowed to do right now, and the client every call goes
 * through.
 *
 * **The client is built here because it needs the session.** Two of its inputs are session state —
 * the double-submit token, which is reissued by six of the eleven authentication routes, and the
 * operator's language — and both are read through getters rather than captured, so a token rotated
 * by a password change is used by the very next request rather than by the one after it.
 *
 * **A refused session raises an overlay, it never navigates.** AD-54's lock is a PIN prompt over
 * whatever the operator was doing, not a logout: a half-filled registration form with somebody
 * standing at the desk must survive it. So `AUTH_SESSION_IDLE_LOCKED` moves this state to `locked`
 * and `AUTH_SESSION_EXPIRED` moves it to `expired`, and in both cases the route beneath stays
 * mounted. That is the whole mechanism behind "an expired session redirects to the lock rather than
 * dropping the operator's in-progress form".
 *
 * **The three session gates are not one state.** `idle_locked` is an overlay, because work is in
 * progress underneath. `password_change` and `totp_enrolment` are full screens, because both can
 * only be met at sign-in — an administrator resetting a password ends that account's sessions, so
 * neither can appear under an operator mid-shift — and a modal over an empty application would be a
 * modal with nothing behind it.
 */

/** What the terminal is doing, from the point of view of who may use it. */
type SessionState =
  | { readonly status: 'anonymous' }
  /** Signed in and unobstructed, or behind a full-screen gate this session names. */
  | { readonly status: 'active'; readonly session: Session }
  /** Signed in, idle too long. The work underneath is intact and a PIN brings it back. */
  | { readonly status: 'locked'; readonly session: Session }
  /**
   * The session is gone, and the last one is remembered.
   *
   * Remembered deliberately: the shell stays mounted around the operator's in-progress work, which
   * is what makes signing in again return them to the same screen rather than to an empty
   * application. The remembered session is used for the shell's chrome and for nothing else — its
   * CSRF token is bound to an identifier the server has destroyed, so `tokenOf` refuses it.
   */
  | { readonly status: 'expired'; readonly session: Session };

export interface SessionContextValue {
  readonly state: SessionState;
  readonly csrfToken: string | null;
  readonly client: ApiClient;
  /** Replaces the session after any route that returns a fresh one, token included. */
  readonly adopt: (session: Session) => void;
  /**
   * Replaces the ACCOUNT inside the current session, for a route that changes the account and
   * deliberately does not reissue a session.
   *
   * Setting the gate PIN is the one such route today: it changes no privilege and rotates nothing,
   * so it answers with the user rather than a session, and the token this client holds is still
   * bound to the id it was issued against. Calling `adopt` with a fabricated session instead would
   * mean inventing an `issuedAt`, an `absoluteExpiresAt` and a CSRF token — three values only the
   * server knows.
   */
  readonly adoptUser: (user: User) => void;
  /** Drops every trace of a session on this terminal. */
  readonly forget: () => void;
  /** Locks this terminal locally, which is what the idle timer trips. */
  readonly lock: () => void;
  /** Hands a fresh anonymous CSRF token to the sign-in form. */
  readonly setAnonymousToken: (token: string) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  fetchImpl,
  initialSession,
  children,
}: Readonly<{
  fetchImpl: typeof globalThis.fetch;
  /**
   * A session this terminal already has, or `null`.
   *
   * Required rather than optional, and nullable rather than absent: "we asked and nobody is signed
   * in" and "nobody thought to ask" are different facts, and a provider that could not tell them
   * apart would start a terminal at the sign-in screen whenever a caller forgot the prop.
   */
  initialSession: Session | null;
  children: ReactNode;
}>): ReactNode {
  const { locale } = useLocale();
  const [state, setState] = useState<SessionState>(() =>
    initialSession === null
      ? { status: 'anonymous' }
      : {
          status: initialSession.gate === 'idle_locked' ? 'locked' : 'active',
          session: initialSession,
        },
  );
  const [anonymousToken, setAnonymousToken] = useState<string | null>(null);

  const onFailure = useCallback((error: ApiError) => {
    if (error.code === 'AUTH_SESSION_IDLE_LOCKED') {
      setState((current) =>
        current.status === 'active' ? { status: 'locked', session: current.session } : current,
      );
      return;
    }
    if (error.code === 'AUTH_SESSION_EXPIRED') {
      setState((current) =>
        current.status === 'anonymous' ? current : { status: 'expired', session: current.session },
      );
    }
  }, []);

  // Rebuilt whenever one of the values it reads changes, rather than reading them through a ref.
  // Rebuilding costs nothing that matters: a request already in flight keeps the token it was sent
  // with, which is correct, and TanStack Query refetches on its KEY rather than on the identity of
  // the function that fetches — so a new client does not restart anything.
  const client = useMemo(
    () =>
      createApiClient({
        fetchImpl,
        locale: () => locale,
        csrfToken: () => tokenOf(state) ?? anonymousToken,
        onFailure,
      }),
    [fetchImpl, onFailure, locale, state, anonymousToken],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      csrfToken: tokenOf(state) ?? anonymousToken,
      client,
      adopt: (session: Session) => {
        setState({
          status: session.gate === 'idle_locked' ? 'locked' : 'active',
          session,
        });
      },
      adoptUser: (user: User) => {
        setState((current) =>
          current.status === 'anonymous'
            ? current
            : { ...current, session: { ...current.session, user } },
        );
      },
      forget: () => {
        setState({ status: 'anonymous' });
        setAnonymousToken(null);
      },
      lock: () => {
        setState((current) =>
          current.status === 'active' ? { status: 'locked', session: current.session } : current,
        );
      },
      setAnonymousToken,
    }),
    [state, anonymousToken, client],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

/**
 * The token to echo, or `null` when the one being held is not usable.
 *
 * An EXPIRED session still carries a token, and sending it would be worse than sending none: it is
 * bound to a session identifier the server has destroyed, so the sign-in that is about to happen
 * would be refused by the CSRF guard rather than by the credentials. The anonymous token fetched
 * for the sign-in form is the right one there.
 */
function tokenOf(state: SessionState): string | null {
  return state.status === 'active' || state.status === 'locked' ? state.session.csrfToken : null;
}

/** The session context. Throws outside the provider, because there is no anonymous default client. */
export function useSessionContext(): SessionContextValue {
  const value = use(SessionContext);
  if (value === null) {
    throw new Error('useSessionContext was called outside SessionProvider');
  }
  return value;
}

/** The API client for the current session. */
export function useApiClient(): ApiClient {
  return useSessionContext().client;
}

/**
 * Whether somebody is actually at this terminal and entitled to use it right now.
 *
 * **What it gates is asking the server anything on a timer.** A locked terminal is refused on every
 * route with a finite idle window, so a poll that keeps running behind the overlay is a refused
 * request every three to five seconds for as long as the lock lasts — spending the per-account
 * budget the gate needs, and re-arming the very error the unlock then has to clear. `presence.
 * occupancy` is `idle: 'none'` and would be answered, but nobody can read a figure behind a modal,
 * so it stops too.
 *
 * It is deliberately NOT the same question as "is this session usable at all": an `expired` session
 * is not active either, and a terminal showing a sign-in over somebody's work should be asking for
 * nothing on a timer for exactly the same reason.
 */
export function useSessionActive(): boolean {
  return useSessionContext().state.status === 'active';
}

/**
 * The session this terminal is showing, or `null` when nobody has ever signed in on it.
 *
 * A locked terminal still HAS a session and still knows whose it is, which is what keeps the
 * operator's name on screen behind the lock — the person coming back needs to see whether the
 * terminal is theirs before they type a PIN into it. An expired one keeps the last session for the
 * same reason and for one more: the shell around the operator's unsaved work has to stay mounted.
 */
export function useSession(): Session | null {
  const { state } = useSessionContext();
  return state.status === 'anonymous' ? null : state.session;
}

/**
 * Fetches an anonymous double-submit token, which the sign-in POST needs.
 *
 * The login route is an unsafe method and is deliberately not exempt from the CSRF guard, so a
 * caller with no session has to be able to obtain a token somewhere; `GET /api/v1/auth/csrf` is
 * that somewhere. Handing one to an anonymous caller costs nothing, because the token still has to
 * be read out of a `SameSite=Strict` cookie on this origin.
 */
export function useAnonymousCsrfToken(): void {
  const { state, csrfToken, client, setAnonymousToken } = useSessionContext();
  const needed = state.status === 'anonymous' || state.status === 'expired';

  useEffect(() => {
    if (!needed || csrfToken !== null) {
      return;
    }
    let live = true;
    void client
      .request(apiRoutes.authCsrf, {})
      .then((issued) => {
        if (live) {
          setAnonymousToken(issued.csrfToken);
        }
      })
      .catch(() => {
        // The sign-in form reports its own failure when the submission is refused. Failing here
        // silently is deliberate: a token that could not be fetched is indistinguishable to the
        // operator from a server that is down, and the form says that in one place.
      });
    return () => {
      live = false;
    };
  }, [needed, csrfToken, client, setAnonymousToken]);
}
